import React, { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductOutlined } from "@ant-design/icons";
import { Button, Image, Modal, Space } from "antd";
import CommonTable from "@/Components/Common/CommonTable/CommonTable";
import TruncatedText from "@/Components/Common/TruncatedText";
import { ToastContext } from "@/Context/ToastContext";

export default function ShopifyImages({ showImageModal, setShowImageModal, closeModals, handleImageSubmit }) {
  const modelRef = useRef();
  const { showToast } = useContext(ToastContext);
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [patentSelectedRows, setPatentSelectedRows] = useState([]);
  const [parentAllRowsSelected, setParentAllRowsSelected] = useState(false);
  const [initialData, setInitialData] = useState([]);

  const extractFilenameFromURL = useCallback((url) => {
    if (url) {
      const pathComponents = url.split("/");
      const lastPathComponent = pathComponents[pathComponents.length - 1].split("?")[0];
      return lastPathComponent;
    }
  }, []);

  // Table columns configuration for Ant Design
  const tableColumns = [
    {
      title: t("imageOptimizer.homePage.Image"),
      dataIndex: "image",
      key: "image",
      width: 120,
      render: (image) => (
        <div style={{ padding: "8px" }}>
          <Image
            src={image?.originalSrc}
            fallback={<ProductOutlined style={{ fontSize: "32px", color: "#d9d9d9" }} />}
            width={64}
            height={64}
            style={{ borderRadius: "4px", objectFit: "cover" }}
            preview={false}
          />
        </div>
      ),
    },
    {
      title: t("imageOptimizer.homePage.Filename"),
      dataIndex: "image",
      key: "filename",
      render: (image) => (
        <TruncatedText text={extractFilenameFromURL(image?.originalSrc)} maxLines={2} maxLength={40} />
      ),
    },
  ];

  const handleSubmit = useCallback(async (rows, selectedResources) => {
    let selectedObjects = rows.find((row) => selectedResources.includes(row.id));
    setData(selectedObjects);
  }, []);

  const handleAddImages = useCallback(async () => {
    if (!data) showToast(t("imageOptimizer.homePage.Please select at least one image"));
    else handleImageSubmit(data);
  }, [data]);

  const handleParentSelectionChange = useCallback(
    (selectionType, isSelecting, selection, _position) => {
      let val = [];
      if (isSelecting) {
        if (selection) val = [selection];
        else val = patentSelectedRows?.length > 0 ? patentSelectedRows : [initialData?.rows?.[0]?.id];
      } else val = [];

      setParentAllRowsSelected(false);
      setPatentSelectedRows([...new Set(val)]);
    },
    [initialData, patentSelectedRows, parentAllRowsSelected]
  );

  return (
    <Modal
      open={showImageModal}
      title="Add Image"
      onCancel={closeModals}
      width={800}
      footer={[
        <Button key="cancel" onClick={closeModals}>
          {t(`gsc.Cancel`)}
        </Button>,
        <Button key="submit" type="primary" onClick={handleAddImages}>
          {t(`imageOptimizer.homePage.Add Images`)}
        </Button>,
      ]}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <CommonTable
          title={t("imageOptimizer.homePage.Other Image")}
          url={"/get/shopify/image"}
          columns={tableColumns}
          ref={modelRef}
          isAdd={false}
          searchKey={["image.originalSrc"]}
          selectable={true}
          isFilterVisible
          isPaginationVisible={false}
          queryPlaceholder="Search Filename"
          handleSubmit={handleSubmit}
          setInitialData={setInitialData}
          handleParentSelectionChange={handleParentSelectionChange}
          patentSelectedRows={patentSelectedRows}
          parentAllRowsSelected={parentAllRowsSelected}
        />
      </Space>
    </Modal>
  );
}
